<template>
  <div class="form-group" :class="{'disabled': parseBoolean(disabled)}">
    <label :for="id" v-if="label" class="form-label">{{ label }}
      <i v-if="parseBoolean(required)" class="far fa-asterisk"></i>
      <span v-if="language" class="language"> ({{ language }})</span>
    </label>
    <input :id="id"
           ref="input"
           type="text"
           inputmode="numeric"
           v-model="computedValue"
           :placeholder="placeholder"
           :maxlength="maxlength"
           class="form-control"
           :class="{'is-invalid': invalid}"
           :disabled="parseBoolean(disabled)"
           @input="handleInput"
           @keyup="handleKeyup"
           @keydown="handleKeydown"
           @click="handleClick"
           @contextmenu="handleContextmenu"
           @focus="handleFocus"
           @blur="handleBlur"/>
    <local-errors :value="errors"/>
    <div v-if="help" class="form-text">
      <slot></slot>
    </div>
  </div>
</template>

<script>
/*
KNOWN BUGS:
- If you have 100,5 and the cursor is after , and you press backspace, the number is changed to 1.005 but the cursor
  is moved to between the 0s. It should have stayed before 5.
- If you have 1,00 and the cursor is between the 0s and you press 5, it will say 1,05 but the cursor will stay
  between the 0 and 5. It should have moved to after 5.
*/
export default {
  name: "input-text",
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String]
    },
    locale: {
      type: String,
      default: "en-US"
    },
    decimalSeparator: {
      type: String,
      default: "."
    },
    groupingSeparator: {
      type: String,
      default: ","
    },
    errors: {
      type: Array,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    language: {
      type: String,
      default: null
    },
    required: {
      type: Boolean
    },
    maxlength: {
      type: Number,
      default: null
    },
    disabled: {
      type: Boolean
    },
  },
  data: function () {
    return {
      selectionStart: 0,
      selectionEnd: 0,
      decimals: false,
      zeros: 0
    };
  },
  computed: {
    help: function () {
      return !!this.$slots.default;
    },
    invalid: function () {
      return this.errors && this.errors.length > 0;
    },
    computedValue: {
      get: function () {
        if (!this.value && this.value != 0) {
          return null;
        }
        const number = this.value;
        let localeString = this.formatNumber(number);
        if (this.decimals && localeString.indexOf(this.decimalSeparator) == -1) {
          localeString += this.decimalSeparator;
          for (let i = 0; i < this.zeros; i++) {
            localeString += "0";
          }
        }
        return localeString;
      },
      set: function (newValue) {
        // Replace all empty whitespaces with a NBSP character. This is the correct character to use and is used
        // by the browser, if we do a "toLocaleString" on a number.
        newValue = newValue.replace(/\s/g, " ");

        // Kind of a hack, but in some countries a decimal separator followed by a dash means ".00". Convert it here.
        const decimalDash = this.decimalSeparator + "-";
        const decimalDashIndex = newValue.indexOf(decimalDash);
        if (decimalDashIndex != -1 && decimalDashIndex == newValue.length - decimalDash.length) {
          newValue = newValue.substring(0, decimalDashIndex) + this.decimalSeparator + "00";
        }

        this.decimals = newValue.indexOf(this.decimalSeparator) != -1;
        if (this.decimals) {
          this.zeros = 0;
          for (let i = newValue.indexOf(this.decimalSeparator) + 1; i < newValue.length; i++) {
            if (newValue[i] == "0") {
              this.zeros++;
            }
          }
        }

        // The current selection.
        const selectionStart = this.$refs.input.selectionStart;
        const selectionEnd = this.$refs.input.selectionEnd;
        const selectionIndex = Math.max(selectionStart, selectionEnd);

        // Clean the input value, so it can be converted to a number. The format this number, so we can find out
        // where the caret should be.
        const noGroupingSeparators = newValue.replaceAll(this.groupingSeparator, "");
        const jsStandard = noGroupingSeparators.replaceAll(this.decimalSeparator, ".")
        const number = isNaN(jsStandard) ? null : parseFloat(jsStandard);
        if (number == null) {
          this.updateValue(newValue);
          return;
        }
        let finalValue = this.formatNumber(number);
        if (this.decimals && finalValue.indexOf(this.decimalSeparator) == -1) {
          finalValue += this.decimalSeparator;
          for (let i = 0; i < this.zeros; i++) {
            finalValue += "0";
          }
        }

        // Calculate the new caret position, based on the previous caret position and the changes made to our input value.
        let newCaretPosition = selectionIndex;
        const selectionStartChange = this.selectionStart - selectionStart;
        const valueLengthChange = finalValue.length - newValue.length;
        newCaretPosition += valueLengthChange;
        if (selectionStartChange > 0 && valueLengthChange > 0) {
          newCaretPosition -= selectionStartChange;
        }
        if (newCaretPosition < 0) {
          newCaretPosition = 0;
        }

        // This is just to refresh the input field, so we get correct formatting.
        // The value we're setting here doesn't really matter.
        this.updateValue("0" + number);

        // First update the value. Then, once it is updated, set the caret position.
        this.$nextTick(() => {
          this.updateValue(number);
          this.$nextTick(() => {
            this.$refs.input.selectionStart = newCaretPosition;
            this.$refs.input.selectionEnd = newCaretPosition;
            this.selectionStart = newCaretPosition;
            this.selectionEnd = newCaretPosition;
          });
        });
      }
    }
  },
  methods: {
    formatNumber: function (value) {
      return value.toLocaleString(this.locale, {
        decimal: this.decimalSeparator,
        groupingSeparator: this.groupingSeparator
      });
    },
    updateValue: function (value) {
      this.$emit("input", value);
    },
    handleInput: function () {
      this.computedValueChange();
    },
    handleKeyup: function (event) {
      this.computedValueChange();
      this.$emit("keyup", event);
    },
    handleKeydown: function (event) {
      this.computedValueChange();
      this.$emit("keydown", event);
    },
    handleClick: function (event) {
      this.computedValueChange();
      this.$emit("click", event);
    },
    handleContextmenu: function (event) {
      this.computedValueChange();
      this.$emit("contextmenu", event);
    },
    handleFocus: function (event) {
      this.computedValueChange();
      this.$emit("focus", event);
    },
    handleBlur: function (event) {
      this.computedValueChange();
      this.$emit("blur", event);
    },
    computedValueChange: function () {
      this.selectionStart = this.$refs.input.selectionStart;
      this.selectionEnd = this.$refs.input.selectionEnd;
    },
  }
}
</script>
